import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ThemeContextInterface,
  ThemeMode,
  ThemePreference,
} from "../../interfaces/theme";
import {
  applyThemeToDocument,
  getInitialPreference,
  resolveTheme,
  saveStoredPreference,
} from "../../utils/themeStorage";
import {
  DARK_THEME,
  LIGHT_THEME,
  PREFERS_DARK_QUERY,
  SYSTEM_THEME,
} from "../../constants/theme";

export const ThemeContext = createContext<ThemeContextInterface>({
  theme: LIGHT_THEME,
  isDarkMode: false,
  toggleTheme: () => null,
  setTheme: () => null,
});

interface ProviderInterface {
  children: React.ReactNode;
}

export default function ThemeProvider({ children }: ProviderInterface) {
  const [preference, setPreference] =
    useState<ThemePreference>(getInitialPreference);
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    resolveTheme(getInitialPreference()),
  );

  useEffect(() => {
    setThemeState(resolveTheme(preference));
  }, [preference]);

  // Until the user picks a theme the panel follows the machine, and keeps
  // following it if the machine changes appearance during the day. Choosing a
  // theme pins it and this stops applying.
  useEffect(() => {
    if (preference !== SYSTEM_THEME) {
      return;
    }
    let query: MediaQueryList;
    try {
      query = window.matchMedia(PREFERS_DARK_QUERY);
    } catch (error) {
      return;
    }
    const followSystem: (event: MediaQueryListEvent) => void = (event) => {
      setThemeState(event.matches ? DARK_THEME : LIGHT_THEME);
    };
    query.addEventListener("change", followSystem);
    return () => query.removeEventListener("change", followSystem);
  }, [preference]);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const setTheme: (next: ThemeMode) => void = useCallback((next) => {
    setPreference(next);
    saveStoredPreference(next);
  }, []);

  const toggleTheme: () => void = useCallback(() => {
    setThemeState((current: ThemeMode) => {
      const next: ThemeMode =
        current === DARK_THEME ? LIGHT_THEME : DARK_THEME;
      setPreference(next);
      saveStoredPreference(next);
      return next;
    });
  }, []);

  const contextValue: ThemeContextInterface = useMemo(
    () => ({
      theme,
      isDarkMode: theme === DARK_THEME,
      toggleTheme,
      setTheme,
    }),
    [theme, toggleTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}
